import React, { useState, useEffect } from "react"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import axios from "axios"
// import { addUserEinkaufsliste } from "./components/products/products"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Alert, Box, LinearProgress, Switch } from "@mui/material"
import AppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import IconButton from "@mui/material/IconButton"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner"
// import SaveIcon from "@mui/icons-material/Save"
import SelfCheckoutCart from "./components/selfCheckout/SelfCheckoutCart"
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart"
import { cartContext } from "./components/selfCheckout/cartContext"
import AddProductBySku from "./components/selfCheckout/AddProductBySku"
import LoadingButton from "@mui/lab/LoadingButton"
import QrScanner from "./components/selfCheckout/QrScanner"
import { SmartScaleProvider, STORAGE_KEY as SMARTSCALE_STORAGE_KEY } from "./contexts/SmartScaleContext.js"

const __ = wp.i18n.__

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#6c5739"
    },
    secondary: {
      main: "#f1ede4"
    },
    POSModeColor: {
      main: "#BEADED"
    },
    background: {
      default: "#fbfbfb",
      paper: "#ffffff"
    },
    success: {
      main: "#00c853"
    }
  },
  components: {
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: "#ffffff"
        }
      }
    }
  }
})

function SelfCheckout() {
  const [blogname, setBlogname] = useState(null)
  const [scanning, setScanning] = useState(true)
  const [scanResult, setScanResult] = useState(null)
  const [adding, setAdding] = useState(false)
  const [productError, setProductError] = useState(null)
  const [productErrorSeverity, setProductErrorSeverity] = useState("warning")
  const [cart, setCart] = useState([])
  const [showCart, setShowCart] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(true)
  const [isPOSAdmin, setIsPOSAdmin] = useState(false)
  const [POSMode, setPOSMode] = useState(false)
  const [margin, setMargin] = useState(0)
  const [selectedMember, setSelectedMember] = useState(null)
  const [selectedPaymentGateway, setSelectedPaymentGateway] = useState(null)
  // const [saveEinkaufsliste, setSaveEinkaufsliste] = useState(false)

  useEffect(() => {
    axios
      .get(`${frontendLocalizer.apiUrl}/pingvin_checkout/v1/getOption?option=blogname`)
      .then(function (response) {
        if (response.data) {
          const res = JSON.parse(response.data)
          setBlogname(res)
        }
      })
      .catch(error => console.log(error))

    if (frontendLocalizer.currentUser.roles.includes("administrator") || frontendLocalizer.currentUser.roles.includes("foodcoop_manager")) {
      setIsPOSAdmin(true)
    }
  }, [])

  useEffect(() => {
    scanning && setProductError(null)
  }, [scanning])

  const cartLoadedRef = React.useRef(false)

  function normalizeWooCartResponse(rawResponse) {
    if (rawResponse === null || rawResponse === undefined) {
      return { data: null, raw: rawResponse, error: "empty_response" }
    }

    let parsed = rawResponse
    if (typeof rawResponse === "string") {
      const trimmed = rawResponse.trim()
      if (!trimmed) {
        return { data: null, raw: rawResponse, error: "empty_response" }
      }

      try {
        parsed = JSON.parse(trimmed)
      } catch (error) {
        return { data: null, raw: rawResponse, error: "invalid_json", message: error.message }
      }
    }

    if (parsed && typeof parsed === "object") {
      if (parsed.success && parsed.data) {
        return { data: parsed.data, raw: parsed, error: null }
      }

      if (parsed.data && typeof parsed.data === "object") {
        return { data: parsed.data, raw: parsed, error: null }
      }

      if (parsed.items) {
        return { data: parsed, raw: parsed, error: null }
      }

      if (Array.isArray(parsed)) {
        return { data: { items: parsed }, raw: parsed, error: null }
      }
    }

    return { data: null, raw: parsed, error: "unexpected_shape" }
  }

  // Load WooCommerce cart on mount
  useEffect(() => {
    // Prevent loading twice
    if (cartLoadedRef.current) return
    cartLoadedRef.current = true

    setLoading(true)

    const currentUserId = frontendLocalizer?.currentUser?.data?.ID ?? frontendLocalizer?.currentUser?.ID ?? 0
    const cartRequestUrl = `${frontendLocalizer.apiUrl}/pingvin_checkout/v1/getWooCommerceCart?ts=${Date.now()}&user=${currentUserId}`

    // Always try to load from WooCommerce cart first (priority over localStorage)
    axios
      .get(cartRequestUrl, {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          "X-Requested-With": "XMLHttpRequest"
        },
        withCredentials: true
      })
      .then(function (response) {
        const normalizedResponse = normalizeWooCartResponse(response.data)

        if (!normalizedResponse.error) {
          const responseData = normalizedResponse.data || {}
          const wooCart = Array.isArray(responseData.items) ? responseData.items : Array.isArray(responseData) ? responseData : []
          const cartCount = typeof responseData.cart_count === "number" ? responseData.cart_count : Array.isArray(wooCart) ? wooCart.length : 0

          if (cartCount > 0 && Array.isArray(wooCart) && wooCart.length > 0) {
            setCart(wooCart)
            localStorage.setItem("fc_selfcheckout_cart", JSON.stringify(wooCart))
          } else {
            localStorage.removeItem("fc_selfcheckout_cart")
            setCart([])
          }
        } else {
          console.error("[SelfCheckout] Response normalization error:", normalizedResponse.error)
          localStorage.removeItem("fc_selfcheckout_cart")
          setCart([])
        }
      })
      .catch(err => {
        console.error("[SelfCheckout] Cart loading error:", err)
        localStorage.removeItem("fc_selfcheckout_cart")
        setCart([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  async function checkout() {
    setSubmitting(true)

    if (cart.length > 0) {
      // await addUserEinkaufsliste(cart)

      try {
        const response = await axios.post(
          `${frontendLocalizer.apiUrl}/pingvin_checkout/v1/addToCart`,
          {
            data: JSON.stringify(cart),
            user: JSON.stringify(frontendLocalizer.currentUser)
          },
          { headers: { "X-WP-Nonce": frontendLocalizer.nonce } }
        )

        setSubmitting(false)
        localStorage.removeItem("fc_selfcheckout_cart")
        location.href = JSON.parse(response.data)
      } catch (error) {
        setSubmitting(false)
        console.error(error)
      }
    } else {
      setProductError("Warenkorb leer.")
      setSubmitting(false)
    }
  }

  function updateScanResult(text) {
    try {
      var data = JSON.parse(text)

      if (data.smartscale) {
        localStorage.setItem(SMARTSCALE_STORAGE_KEY, data.smartscale)
        setProductError(__("Connected to scale", "pingvin_checkout"))
        setProductErrorSeverity("success")
        setShowCart(true)
        setScanning(false)
        setScanResult(null) // Reset scan result
        return
      }
    } catch (e) {}

    setScanResult(text)
    setAdding(true)
    setScanning(false)
  }

  // Reset scanResult when user finishes adding product
  useEffect(() => {
    if (!adding && showCart) {
      // When AddProductBySku closes and cart is shown, reset the scan result
      setScanResult(null)
    }
  }, [adding, showCart])

  function posCheckout() {
    setSubmitting(true)

    if (cart.length > 0) {
      axios
        .post(
          `${frontendLocalizer.apiUrl}/pingvin_checkout/v1/postCreatePOSorder`,
          {
            pos_user: frontendLocalizer.currentUser.data.ID,
            type: selectedMember ? "memberOrder" : "guestOrder",
            cart: JSON.stringify(cart),
            user: selectedMember ? JSON.stringify(selectedMember) : JSON.stringify(frontendLocalizer.currentUser.data),
            payment_gateway: JSON.stringify(selectedPaymentGateway)
          },
          {
            headers: {
              "X-WP-Nonce": frontendLocalizer.nonce
            }
          }
        )
        .then(function (response) {
          setCart([])
          localStorage.removeItem("fc_selfcheckout_cart")
        })
        .catch(error => console.log(error.message))
        .finally(response => {
          setSubmitting(false)
          setShowCart(false)
          setScanning(false)
          setAdding(true)
        })
    } else {
      setProductError("Warenkorb leer.")
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (POSMode) {
      setScanning(false)
      setAdding(true)
      setShowCart(false)
    }
  }, [POSMode])

  let buttons = {
    pos: isPOSAdmin && showCart,
    scan: !POSMode && showCart,
    add: showCart,
    cart: !showCart,
    save: !POSMode && showCart && cart.length > 0,
    checkout: showCart && cart.length > 0
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <SmartScaleProvider>
          <cartContext.Provider value={{ cart, setCart }}>
            {active ? (
              <Dialog fullScreen open={true} maxWidth="lg" scroll="paper" aria-labelledby="scroll-dialog-title" aria-describedby="scroll-dialog-description">
                <AppBar sx={{ position: "relative" }} color={POSMode ? "POSModeColor" : "primary"}>
                  <Toolbar sx={{ justifyContent: "space-between" }}>
                    <DialogTitle textAlign="left" sx={{ fontSize: "1rem" }}>
                      {blogname} - {!POSMode ? __("Self Checkout", "fcplugin") : __("Point of Sale", "fcplugin")}
                    </DialogTitle>
                    <Stack justifyContent={"flex-end"} alignItems={"center"} direction={"row"}>
                      <span style={{ marginRight: "25px" }}>{frontendLocalizer.name} </span>
                      <IconButton edge="start" color="inherit" aria-label="close" onClick={() => (window.location.href = frontendLocalizer.homeUrl)}>
                        <ExitToAppIcon />
                      </IconButton>
                    </Stack>
                  </Toolbar>
                </AppBar>
                {productError && (
                  <Alert sx={{ margin: 1 }} severity={productErrorSeverity}>
                    {productError}
                  </Alert>
                )}
                <DialogContent dividers={scroll === "paper"} sx={{ padding: 2, boxSizing: "content-box" }}>
                  {loading && (
                    <Box sx={{ width: "100%", marginBottom: "10px" }}>
                      <LinearProgress />
                    </Box>
                  )}
                  {scanning && !POSMode && <QrScanner updateScanResult={updateScanResult} />}
                  {adding && <AddProductBySku setShowCart={setShowCart} setAdding={setAdding} scanResult={scanResult} POSMode={POSMode} />}
                  {showCart && <SelfCheckoutCart setScanning={setScanning} setShowCart={setShowCart} POSMode={POSMode} margin={margin} selectedMember={selectedMember} setSelectedMember={setSelectedMember} selectedPaymentGateway={selectedPaymentGateway} setSelectedPaymentGateway={setSelectedPaymentGateway} />}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: "#f0f0f0" }}>
                  {buttons.pos && (
                    <Box sx={{ marginRight: 2 }}>
                      <Switch checked={POSMode} onChange={event => setPOSMode(event.target.checked)} inputProps={{ "aria-label": "pos-mode" }} color={POSMode ? "POSModeColor" : "primary"} /> {__("POS Modus", "fcplugin")}
                    </Box>
                  )}
                  <Button
                    disabled={submitting}
                    variant="contained"
                    size="large"
                    color={POSMode ? "POSModeColor" : "primary"}
                    onClick={() => {
                      setShowCart(false)
                      setScanning(true)
                      setAdding(false)
                    }}
                  >
                    <Stack justifyContent={"center"} alignItems={"center"} direction={"row"} gap={1}>
                      <QrCodeScannerIcon /> <span>{__("Add", "pingvin_checkout")}</span>
                    </Stack>
                  </Button>
                  <Button
                    disabled={submitting}
                    variant="contained"
                    size="large"
                    color={POSMode ? "POSModeColor" : "primary"}
                    onClick={() => {
                      setShowCart(true)
                      setScanning(false)
                      setAdding(false)
                    }}
                  >
                    <Stack justifyContent={"center"} alignItems={"center"} direction={"row"} gap={1}>
                      <ShoppingCartIcon /> <span>{__("Cart", "pingvin_checkout")}</span>
                    </Stack>
                  </Button>
                  {/* {buttons.save && (
                    <Button
                      disabled={submitting}
                      variant="contained"
                      size="large"
                      color={POSMode ? "POSModeColor" : "primary"}
                      onClick={() => {
                        setSaveEinkaufsliste(true)
                      }}
                    >
                      <SaveIcon />
                    </Button>
                  )} */}
                  {buttons.checkout && (
                    <LoadingButton
                      id="pingvin_checkout_button"
                      startIcon={<PointOfSaleIcon />}
                      variant="contained"
                      size="large"
                      color={POSMode ? "POSModeColor" : "primary"}
                      loading={submitting}
                      onClick={() => {
                        POSMode ? posCheckout() : checkout()
                      }}
                    >
                      {!POSMode ? __("Pay", "pingvin_checkout") : __("Complete purchase", "pingvin_checkout")}
                    </LoadingButton>
                  )}
                </DialogActions>
              </Dialog>
            ) : active === null ? (
              <Box sx={{ width: "100%", marginBottom: 4 }}>
                <LinearProgress />
              </Box>
            ) : (
              <Alert severity="error" sx={{ marginBottom: 4 }}>
                {__("Der Self Checkout ist deaktiviert.", "fcplugin")}
              </Alert>
            )}
          </cartContext.Provider>
        </SmartScaleProvider>
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default SelfCheckout
